'use client'

import { HorizontalScroll } from 'fukku/HorizontalScroll'
import { HorizontalScrollTextType } from 'fukku/HorizontalScroll/components/HorizontalScrollText/HorizontalScrollText'
import { ClientHelpModule } from 'help/components/HelpModule/client/ClientHelpModule'
import type { GetQuestionResponse, QuestionIDType } from 'help/types/types'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { MyPurchasesClientLink } from 'links/myPurchases/client'
import { SinglePurchaseClientLink } from 'links/singlePurchase/client'
import { useUserDetails } from 'user-data/hooks/useUserDetails'

import { useMyAccount } from '../../contexts/MyAccountProvider'
import { useStatus } from '../../hooks/useStatus'
import { getAddres } from '../../utils/getAddress/getAddress'
import { getFilteredQuestions } from '../../utils/getFilteredQuestions/getFilteredQuestions'
import { getPurchaseQuestionsIds } from '../../utils/getPurchaseQuestionsIds/getPurchaseQuestionsIds'
import { purchaseStatusInfo } from '../../utils/purchaseStatusInfo/purchaseStatusInfo'
import { ModalHelp } from '../ModalHelp/ModalHelp'
import { OrderImages } from './OrderImages/OrderImages'

import styles from './ActivePurchaseCarousel.module.scss'
import text from 'fukku/styles/texts.module.scss'

export const ActivePurchaseCarousel = ({
	allQuestions,
}: {
	readonly allQuestions: (GetQuestionResponse | null)[]
}) => {
	const {
		userInfo: { activePurchases },
	} = useMyAccount()
	const { t } = useLabels()
	const getStatus = useStatus()
	const userDetails = useUserDetails()

	if (!activePurchases.length) {
		return null
	}
	const lastPurchaseKey =
		activePurchases.length > 1
			? 'profile.lastPurchase.plural.title'
			: 'profile.lastPurchase.title'

	const lastOrder = activePurchases[0].orders[0]
	const questionsIds: QuestionIDType[] = getPurchaseQuestionsIds({ lastOrder })

	const filteredQuestions = getFilteredQuestions({
		isLoyalty: userDetails?.loyalty,
		allQuestions,
		questionsIds,
	})

	return (
		<>
			<div className={styles.carouselContainer}>
				<MyPurchasesClientLink className={styles.linkMyPurchases}>
					<h2 className={`${text.uppercaseL} ${styles.lastPurchaseLabel}`}>
						{t(lastPurchaseKey)}
					</h2>
				</MyPurchasesClientLink>
				<div className={styles.activePurchases}>
					{activePurchases.map((activePurchase) => {
						const lastOrderPurchase = activePurchase.orders[0]
						const { title, status } =
							purchaseStatusInfo[lastOrderPurchase.status]
						const address = getAddres({ lastOrder: lastOrderPurchase })

						const updatedStatus = getStatus({
							activePurchase,
							status,
						})

						return (
							<HorizontalScroll
								key={activePurchase.creationDateTime}
								className={styles.purchaseImages}
								data-testid={activePurchase.purchaseId}
								titleProps={{
									content: t(title),
									className: text.uppercaseS,
									type: HorizontalScrollTextType.H3,
								}}
								subtitleProps={{
									content: address ? address : '',
									className: text.bodyS,
								}}
								status={updatedStatus}
								showPaddingHeader={false}
								showPaddingCarousel={false}
							>
								<SinglePurchaseClientLink
									purchaseId={activePurchase.purchaseId}
									className={styles.linkMyPurchases}
								>
									<OrderImages lastOrder={lastOrderPurchase} />
								</SinglePurchaseClientLink>
							</HorizontalScroll>
						)
					})}
				</div>
			</div>
			<ClientHelpModule
				titleKey='profile.help.title'
				moreKey='profile.more.link'
			>
				{filteredQuestions.map((question) => (
					<ModalHelp
						question={question}
						key={`${question?.question.key}-${question?.question.categoryKey}`}
					/>
				))}
			</ClientHelpModule>
		</>
	)
}
